import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import AnimatedSection from '../components/common/AnimatedSection';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Cpu } from 'lucide-react';

const ProjectsPage = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const content = t('projectsPage');

  const sectionData = [
    {
      icon: Shield,
      color: 'bg-[#8B2323]/10',
      iconColor: 'text-[#8B2323]',
      accentColor: 'border-[#8B2323]'
    },
    {
      icon: Users,
      color: 'bg-[#5B2D8E]/10',
      iconColor: 'text-[#5B2D8E]',
      accentColor: 'border-[#5B2D8E]'
    },
    {
      icon: Cpu,
      color: 'bg-[#F5A623]/10',
      iconColor: 'text-[#D4880D]',
      accentColor: 'border-[#F5A623]'
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className={`section ${isDark ? 'bg-gradient-to-b from-[#1C1917] to-[#0C0A09]' : 'bg-gradient-to-b from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <span className="caption mb-4 block">Projection</span>
              <div className="divider mx-auto mb-8" />
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <h1 className="heading-display mb-8">
                {content.title}
              </h1>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <p className="body-large max-w-2xl mx-auto">
                Trois axes stratégiques pour préparer l'avenir de la nation.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Triptyque Sections */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {content.sections.map((section, index) => {
                const { icon: Icon, color, iconColor, accentColor } = sectionData[index];
                return (
                  <AnimatedSection key={index} delay={index * 0.15}>
                    <div className={`h-full p-8 border-t-4 rounded-2xl border ${accentColor} ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-stone-200'}`}>
                      <div className={`w-16 h-16 ${color} rounded-2xl flex items-center justify-center mb-6`}>
                        <Icon className={`w-8 h-8 ${iconColor}`} />
                      </div>
                      <h3 className="heading-3 mb-6">{section.title}</h3>
                      <ul className="space-y-4">
                        {section.points.map((point, pIndex) => (
                          <li key={pIndex} className="flex items-start gap-3">
                            <span className={`w-1.5 h-1.5 rounded-full ${color.replace('/10', '')} mt-2`} />
                            <span className="text-theme-secondary">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Detail Sections */}
      {content.sections.map((section, index) => {
        const { icon: Icon, color, iconColor } = sectionData[index];
        return (
          <section 
            key={index} 
            className={`section ${index % 2 === 0 
              ? isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]' 
              : isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'
            }`}
          >
            <div className="container mx-auto px-4 lg:px-10">
              <div className="max-w-4xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <AnimatedSection className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                    <div className={`aspect-square ${color} rounded-2xl flex items-center justify-center`}>
                      <Icon className={`w-32 h-32 ${iconColor} opacity-50`} />
                    </div>
                  </AnimatedSection>
                  
                  <AnimatedSection delay={0.2} className={index % 2 !== 0 ? 'lg:order-1' : ''}>
                    <h2 className="heading-2 mb-6">{section.title}</h2>
                    <div className="divider mb-8" />
                    <ul className="space-y-6">
                      {section.points.map((point, pIndex) => (
                        <li key={pIndex} className="flex items-start gap-4">
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#5B2D8E]/20' : 'bg-[#5B2D8E]/10'}`}>
                            <span className={`text-sm font-semibold ${isDark ? 'text-[#7C4DAF]' : 'text-[#5B2D8E]'}`}>{pIndex + 1}</span>
                          </span>
                          <p className="text-lg text-theme-secondary" style={{ fontFamily: 'var(--font-serif)' }}>
                            {point}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </AnimatedSection>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="section bg-[#5B2D8E]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-1 text-white mb-6">
                L'avenir se construit maintenant
              </h2>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <p className="text-xl text-white/80 mb-10" style={{ fontFamily: 'var(--font-serif)' }}>
                Rejoignez une vision qui prend le temps long.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <Link 
                to="/rejoindre" 
                className="btn-secondary"
              >
                Rejoindre l'ADA
                <ArrowRight className="w-5 h-5" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
